import { PaperPlaneRightIcon } from '@phosphor-icons/react';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Chat.module.css';

interface ChatComposerProps {
  value: string;
  maxLength: number;
  /** Ввод заблокирован, пока обрабатывается предыдущее сообщение (§10). */
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
}

/** Максимальная высота поля до появления собственной прокрутки. */
const MAX_TEXTAREA_HEIGHT = 180;

/**
 * Поле ввода сообщения (1–2000 символов, MVP_TECH_RFC §10).
 *
 * Пока предыдущее сообщение обрабатывается, поле заблокировано: второе
 * сообщение в ту же сессию backend отверг бы с `409 SEARCH_MESSAGE_IN_PROGRESS`,
 * и лучше не дать его отправить, чем показать ошибку.
 *
 * Enter отправляет, Shift+Enter переносит строку — привычное поведение чата.
 * Высота растёт под текст: длинный запрос с главной не должен прятаться в
 * одну строку.
 *
 * Кнопка отправки — круглая и **всегда красная** (DESIGN_SYSTEM §5.1): это
 * единственное исключение из правила «одна красная кнопка на экран», и
 * выглядит она одинаково везде, где живёт диалог. Отключённая — тот же
 * красный, только бледнее, и причина стоит в `title`: отключённая кнопка не
 * молчит.
 */
export function ChatComposer({
  value,
  maxLength,
  disabled,
  onChange,
  onSubmit,
}: ChatComposerProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    node.style.height = 'auto';
    // У поля своя рамка: `scrollHeight` её не включает, и без поправки на
    // рамку однострочное поле получало бы прокрутку в два пикселя.
    const frame = node.offsetHeight - node.clientHeight;
    node.style.height = `${String(Math.min(node.scrollHeight + frame, MAX_TEXTAREA_HEIGHT))}px`;
  }, [value]);

  const trimmed = value.trim();
  const canSubmit = !disabled && trimmed.length > 0 && trimmed.length <= maxLength;
  const disabledReason = disabled
    ? t('chat.composer.waiting')
    : canSubmit
      ? undefined
      : t('chat.composer.empty');

  return (
    <form
      className={styles.composer}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) onSubmit();
      }}
    >
      <label className="visually-hidden" htmlFor={fieldId}>
        {t('chat.composer.label')}
      </label>
      <div className={styles.composerRow}>
        <textarea
          id={fieldId}
          ref={textareaRef}
          className={styles.composerInput}
          rows={1}
          maxLength={maxLength}
          value={value}
          disabled={disabled}
          placeholder={disabled ? t('chat.composer.waiting') : t('chat.composer.placeholder')}
          aria-describedby={`${fieldId}-hint`}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey) return;
            event.preventDefault();
            if (canSubmit) onSubmit();
          }}
        />
        <button
          type="submit"
          className={styles.send}
          disabled={!canSubmit}
          aria-label={t('chat.composer.send')}
          title={disabledReason}
        >
          <PaperPlaneRightIcon size={20} aria-hidden="true" />
        </button>
      </div>

      <div className={styles.composerMeta}>
        <p id={`${fieldId}-hint`} className={styles.composerHint}>
          {t('chat.composer.hint')}
        </p>
        <span className={`${styles.composerCount} tabular`} aria-hidden="true">
          {trimmed.length} / {maxLength}
        </span>
      </div>
    </form>
  );
}
